import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { PackageStatusBadge } from "@/components/StatusBadge";
import { toast } from "sonner";
import { Search, Eye, ChevronUp, ChevronDown } from "lucide-react";
import PackageDetailDialog from "@/components/PackageDetailDialog";

const PAGE_SIZE = 8;

const TITLES = {
  WAITING_FOR_PICKUP: "Packages Waiting for Pickup",
  PICKED_UP_BY_RIDER: "Packages Picked Up by Rider",
  RECEIVED_AT_WAREHOUSE: "Packages Received at Warehouse",
  SHIPPED_OUT: "Packages Shipped Out from Warehouse",
  OUT_FOR_LAST_MILE: "Packages Out for Last-Mile Delivery",
  DELIVERED: "Packages Delivered to Customer",
};

export default function Packages({ status }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState({ key: "created_at", dir: "desc" });
  const [page, setPage] = useState(1);
  const [openPackageId, setOpenPackageId] = useState(null);

  const load = () => {
    setLoading(true);
    api
      .get("/admin/packages", { params: { status, search: search || undefined } })
      .then((r) => setRows(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setPage(1); load(); }, [status]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const sorted = useMemo(() => {
    const arr = [...rows];
    arr.sort((a, b) => {
      const av = (a[sort.key] || "").toString();
      const bv = (b[sort.key] || "").toString();
      return sort.dir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
    });
    return arr;
  }, [rows, sort]);

  const paged = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));

  const toggleSort = (key) =>
    setSort((s) => ({ key, dir: s.key === key && s.dir === "asc" ? "desc" : "asc" }));

  const SortHeader = ({ k, children }) => (
    <button
      onClick={() => toggleSort(k)}
      data-testid={`sort-${k}`}
      className="inline-flex items-center gap-1 hover:text-slate-900 hover:dark:text-white"
    >
      {children}
      {sort.key === k ? (sort.dir === "asc" ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />) : null}
    </button>
  );

  return (
    <div data-testid="packages-page" className="space-y-6">
      <div>
        <div className="text-xs font-mono-tech uppercase tracking-[0.2em] text-cyan-400 mb-2">
          / Packages
        </div>
        <h1 className="font-display text-3xl font-bold tracking-tight">{TITLES[status]}</h1>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input
            data-testid="search-input"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search by package ID, store, receiver, rider…"
            className="pl-10 bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white focus-visible:ring-cyan-400"
          />
        </div>
        <div className="text-xs font-mono-tech text-slate-500">
          {loading ? "Loading…" : `${rows.length} package${rows.length === 1 ? "" : "s"}`}
        </div>
      </div>

      <div className="border border-slate-200 dark:border-slate-800 rounded-lg overflow-hidden bg-slate-100/40 dark:bg-slate-900/40">
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full text-sm min-w-[1100px]">
            <thead className="bg-slate-100/80 dark:bg-slate-900/80 border-b border-slate-200 dark:border-slate-800 text-xs font-mono-tech uppercase tracking-wider text-slate-600 dark:text-slate-400">
              <tr>
                <th className="text-left px-5 py-3"><SortHeader k="package_id">Package ID</SortHeader></th>
                <th className="text-left px-5 py-3">Description</th>
                <th className="text-left px-5 py-3">Store</th>
                <th className="text-left px-5 py-3">Receiver</th>
                <th className="text-left px-5 py-3">Rider</th>
                <th className="text-left px-5 py-3">Status</th>
                <th className="text-left px-5 py-3"><SortHeader k="created_at">Created</SortHeader></th>
                <th className="text-right px-5 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paged.map((p) => (
                <tr
                  key={p.id}
                  data-testid={`package-row-${p.package_id}`}
                  className="border-b border-slate-200/60 dark:border-slate-800/60 hover:bg-slate-100/60 hover:dark:bg-slate-900/60 transition-colors"
                >
                  <td className="px-5 py-3 text-cyan-300 font-mono-tech text-xs">{p.package_id}</td>
                  <td className="px-5 py-3 text-slate-200 max-w-[220px] truncate">{p.package_name}</td>
                  <td className="px-5 py-3 text-slate-600 dark:text-slate-400">
                    <div className="text-slate-200">{p.store_name}</div>
                    <div className="text-xs text-slate-500">{p.store_owner_name}</div>
                  </td>
                  <td className="px-5 py-3 text-slate-600 dark:text-slate-400">
                    <div className="text-slate-200">{p.receiver_name}</div>
                    <div className="text-xs text-slate-500">{p.receiver_phone}</div>
                  </td>
                  <td className="px-5 py-3 text-slate-700 dark:text-slate-300">{p.assigned_rider_name || <span className="text-slate-500 dark:text-slate-600">—</span>}</td>
                  <td className="px-5 py-3"><PackageStatusBadge status={p.status} /></td>
                  <td className="px-5 py-3 text-slate-600 dark:text-slate-400 font-mono-tech text-xs">
                    {new Date(p.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-5 py-3 text-right whitespace-nowrap">
                    <Button
                      data-testid={`view-package-${p.package_id}`}
                      size="sm"
                      variant="outline"
                      onClick={() => setOpenPackageId(p.package_id)}
                      className="h-8 bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-200 hover:bg-slate-200 hover:dark:bg-slate-800 hover:text-slate-900 hover:dark:text-white"
                    >
                      <Eye className="w-3.5 h-3.5 mr-1.5" />
                      View Details
                    </Button>
                  </td>
                </tr>
              ))}
              {!loading && paged.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-5 py-16 text-center text-slate-500">
                    <div className="text-base font-display font-semibold mb-1 text-slate-700 dark:text-slate-300">No packages at this stage</div>
                    <div className="text-xs">Packages will appear here once they reach this status.</div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex items-center justify-between text-xs text-slate-600 dark:text-slate-400">
        <div className="font-mono-tech">
          Showing {sorted.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1}–
          {Math.min(page * PAGE_SIZE, sorted.length)} of {sorted.length}
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid="pagination-prev"
            size="sm"
            variant="outline"
            disabled={page <= 1}
            onClick={() => setPage(page - 1)}
            className="h-8 bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 hover:dark:bg-slate-800 hover:text-slate-900 hover:dark:text-white"
          >
            Prev
          </Button>
          <span className="font-mono-tech">{page} / {totalPages}</span>
          <Button
            data-testid="pagination-next"
            size="sm"
            variant="outline"
            disabled={page >= totalPages}
            onClick={() => setPage(page + 1)}
            className="h-8 bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 hover:dark:bg-slate-800 hover:text-slate-900 hover:dark:text-white"
          >
            Next
          </Button>
        </div>
      </div>

      <PackageDetailDialog
        packageId={openPackageId}
        onOpenChange={(o) => !o && setOpenPackageId(null)}
        onUpdated={load}
      />
    </div>
  );
}
